import { isGuid } from 'src/app/_helpers/guid.helper';

/**
 * Read a cached entity selection (selected organization / parameter) out of localStorage, evicting it when the
 * blob predates the data-residency PK->UUID migration.
 *
 * These keys cache a whole entity object, and `selectedOrg.id` is replayed into `filterBy.organizationLevelId`
 * on nearly every grid request. A blob cached before the migration still holds an **integer** id, which the API
 * rejects as an invalid Guid — the same 400 as a stale stored payload, but with no database row to repair, and
 * it survives until the user logs out (`AuthService.logout` is the only thing that clears these keys).
 *
 * Validating the id on read makes the cache self-healing: one discarded read and the next selection writes a
 * UUID. Both cached entities (`OrganizationLevel`, `Parameter`) have UUID primary keys, so asserting the shape
 * cannot evict a currently-valid blob.
 */
export function readCachedSelection<T extends { id?: unknown }>(key: string): T | null {
  const raw = localStorage.getItem(key);
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    localStorage.removeItem(key);
    return null;
  }

  if (!parsed || typeof parsed !== 'object' || !isGuid((parsed as { id?: unknown }).id)) {
    localStorage.removeItem(key);
    return null;
  }

  return parsed as T;
}
